import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCircle2, ChevronRight, KeyRound, LockKeyhole, LogOut, ShieldCheck, SlidersHorizontal, WalletCards } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { getSessionInfo } from "@/lib/session.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/owner/settings")({
  head: () => ({ meta: [{ title: "Owner settings — Shields" }, { name: "robots", content: "noindex" }] }),
  component: OwnerSettings,
});

const destinations = [
  { to: "/owner/approvals" as const, label: "Approvals", detail: "Review pending decisions", icon: CheckCircle2 },
  { to: "/owner/automation" as const, label: "Automation", detail: "Review jobs, alerts and recovery", icon: SlidersHorizontal },
  { to: "/owner/payments" as const, label: "Payments", detail: "View the disabled payment gate and records", icon: WalletCards },
  { to: "/owner/notifications" as const, label: "Notifications", detail: "Read your private Owner updates", icon: Bell },
];

function OwnerSettings() {
  const query = useQuery({ queryKey: ["session-info"], queryFn: () => getSessionInfo(), retry: false });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  async function signOut() {
    try {
      await queryClient.cancelQueries();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      queryClient.clear();
      await navigate({ to: "/auth", replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not sign out");
    }
  }

  return <div className="min-h-screen bg-background pb-28 text-foreground">
    <AppHeader workspace="owner" />
    <main className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6">
      <header className="rounded-3xl bg-[#071a38] p-6 text-white sm:p-8">
        <span className="inline-flex size-12 items-center justify-center rounded-2xl border border-[#d7ae65]/50 text-[#efd59f]"><ShieldCheck className="size-6" aria-hidden /></span>
        <p className="mt-6 text-xs font-bold uppercase tracking-[.18em] text-[#efd59f]">Private workspace</p>
        <h1 className="mt-2 text-3xl font-extrabold">Owner settings</h1>
        <p className="mt-2 text-sm text-white/75">Account access, operational controls and setup status in one place.</p>
      </header>

      {query.isLoading ? <p className="rounded-2xl border border-border bg-card p-5 text-sm" role="status">Checking account access…</p>
        : query.error || !query.data ? <div role="alert" className="rounded-2xl border border-border bg-card p-5"><p>Owner account information is unavailable.</p><Button className="mt-3" onClick={() => void query.refetch()}>Retry</Button></div>
        : <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-bold">Signed-in account</h2>
          <p className="mt-2 break-all text-sm text-muted-foreground">{query.data.email || "Email unavailable"}</p>
          <p className="mt-3 flex items-center gap-2 text-sm"><LockKeyhole className="size-4 text-[#b88737]" aria-hidden />Owner access is checked on the server.</p>
          <Link to="/account" className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold hover:bg-muted"><KeyRound className="size-4" aria-hidden />Account and security</Link>
        </section>}

      <section className="rounded-2xl border border-[#ead6ac] bg-[#fff9ed] p-5 text-sm text-[#765218]">
        <h2 className="font-bold">Payment setup remains locked</h2>
        <p className="mt-2 leading-6">Keep payments off until a working business-email sign-in is verified and isolated Stripe sandbox checks are complete. This screen does not activate payments.</p>
      </section>

      <section aria-label="Owner controls" className="space-y-2">
        {destinations.map(({ to, label, detail, icon: Icon }) => <Link key={to} to={to} className="flex min-h-16 items-center gap-4 rounded-2xl border border-border bg-card p-4 transition hover:border-[#d7ae65] hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#153e80]">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#f4f7fb] text-[#153e80]"><Icon className="size-5" aria-hidden /></span>
          <span className="min-w-0"><strong className="block text-sm">{label}</strong><span className="text-xs text-muted-foreground">{detail}</span></span>
          <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden />
        </Link>)}
      </section>
      <Button variant="outline" onClick={() => void signOut()} className="min-h-11 w-full"><LogOut className="mr-2 size-4" aria-hidden />Sign out</Button>
    </main>
  </div>;
}
